// «Ricerche salvate», the side panel of the search, as the design «Pagina Ricerca»
// draws it: the folders (a folder icon, open when the folder is), and in each the
// searches, a white field with the name and, under it, the tags of its choices,
// a line between them; the searches without a folder after the folders; at the
// bottom nothing more (the guide is the «?» of the form). A search opens in the form, not sent (decision 44:
// it can be changed before «Cerca»); its menu renames it, moves it to a folder or deletes it (with the
// confirmation in place). A folder is only a label of its searches (Loggione,
// decision 24 of 2/11): it exists while one of them has it. «Rinomina la
// cartella» gives the new name to each of its searches (decision 42).
// SaveSearch, the box of «Salva ricerca», lives here with the list it fills.
import { useState } from "react";
import { Button, Dropdown, Input, Label, Separator, TextField } from "@heroui/react";
import { Link } from "react-router";

import { Icon } from "../../components/Icon";
import { PanelStatus, STATUS_BUTTON_CLASS } from "../../components/PanelStatus";
import { PanelToggle } from "../../components/NewsLayout";
import { SidebarSection } from "../../components/sidebar/SidebarSection";
import {
  NAME_TAKEN,
  nameTaken,
  savedDraftState,
  savedSearchError,
  savedSearchFolders,
  savedSearchTags,
  type SavedAction,
  type SavedSearch,
} from "./adapt";
import { useChangeSavedSearch, useCreateSavedSearch, useDeleteSavedSearch, useSavedSearches } from "./api";
import { fromSavedQuery, toSavedQuery, writeSearchParams, type SearchParams } from "./params";

export const SAVED_SEARCHES_TITLE = "Ricerche salvate";
export const LOADING_SAVED = "Caricamento delle ricerche salvate…";
export const SAVED_FAILED = "Ricerche salvate non disponibili.";
export const NO_SAVED = "Nessuna ricerca salvata: «Salva ricerca» la porta qui.";
export const CONFIRM_DELETE = "Eliminare la ricerca salvata?";
export const SAVED_OK = "Ricerca salvata: la trovi in «Ricerche salvate».";
export const FOLDER_JOINS = "C’è già una cartella con questo nome: le ricerche finiranno tutte lì.";

type Mode =
  | { kind: "idle" }
  | { kind: "rename"; id: string }
  | { kind: "move"; id: string }
  | { kind: "confirm-delete"; id: string }
  | { kind: "rename-folder"; folder: string };
type Failure = { id: string; message: string; onRetry?: () => void };

/** A text field of the panel: the name of a search, its folder. */
export function NameField({
  label,
  value,
  onChange,
  isDisabled,
  maxLength = 80,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  maxLength?: number;
}) {
  return (
    <TextField value={value} onChange={onChange} isDisabled={isDisabled} className="flex w-full flex-col gap-1">
      <Label className="text-sm leading-6 font-medium text-accent">{label}</Label>
      <Input
        placeholder={label}
        maxLength={maxLength}
        className="h-9 w-full rounded-xl border-0 bg-field-background px-3 text-sm leading-5 shadow-field"
      />
    </TextField>
  );
}

/** The folders the caller has, as quick choices under the field of a folder. */
function FolderChoices({ folders, onPick }: { folders: string[]; onPick: (folder: string) => void }) {
  if (folders.length === 0) return null;
  return (
    <div role="group" aria-label="Cartelle esistenti" className="flex flex-wrap items-center gap-1.5">
      {folders.map((folder) => (
        <button
          key={folder}
          type="button"
          onClick={() => onPick(folder)}
          className="flex h-6 cursor-pointer items-center gap-1 rounded-xl bg-white px-2 text-xs leading-5 font-medium text-field-foreground shadow-field"
        >
          <Icon name="folder" className="size-3" />
          {folder}
        </button>
      ))}
    </div>
  );
}

const foldersOf = (items: readonly SavedSearch[]) => savedSearchFolders(items).folders.map((f) => f.name);

/** «Salva ricerca»: the name, the folder (one already there or a new one), then POST /saved-searches. */
export function SaveSearch({ params, onDone }: { params: SearchParams; onDone: () => void }) {
  const saved = useSavedSearches();
  const create = useCreateSavedSearch();
  const [name, setName] = useState(params.q.slice(0, 80));
  const [folder, setFolder] = useState("");
  const [done, setDone] = useState(false);
  const trimmed = name.trim();
  const taken = trimmed !== "" && nameTaken(saved.data?.items ?? [], trimmed);
  const send = () =>
    create.mutate(
      { name: trimmed, query: toSavedQuery(params), folder: folder.trim() || null },
      { onSuccess: () => setDone(true) },
    );
  const failure = create.isError ? savedSearchError(create.error, "create") : null;

  if (done) {
    return (
      <PanelStatus indent={false} role="status">
        {SAVED_OK}
      </PanelStatus>
    );
  }
  return (
    <div className="flex flex-col gap-2.5 rounded-lg bg-white px-[14px] py-[13px] shadow-field">
      <NameField label="Nome della ricerca" value={name} onChange={setName} isDisabled={create.isPending} />
      {taken && <PanelStatus role="note">{NAME_TAKEN}</PanelStatus>}
      <NameField
        label="Cartella (facoltativa)"
        value={folder}
        onChange={setFolder}
        isDisabled={create.isPending}
        maxLength={40}
      />
      <FolderChoices folders={foldersOf(saved.data?.items ?? [])} onPick={setFolder} />
      {failure && (
        <PanelStatus role="alert" indent={false} onRetry={failure.retry ? send : undefined}>
          {failure.message}
        </PanelStatus>
      )}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          size="sm"
          isDisabled={trimmed === "" || taken || create.isPending}
          onPress={send}
          className={STATUS_BUTTON_CLASS}
        >
          Salva
        </Button>
        <Button size="sm" isDisabled={create.isPending} onPress={onDone} className={STATUS_BUTTON_CLASS}>
          Annulla
        </Button>
      </div>
    </div>
  );
}

function Rename({
  search,
  others,
  onSave,
  onCancel,
}: {
  search: SavedSearch;
  others: SavedSearch[];
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(search.name);
  const trimmed = name.trim();
  const taken = trimmed !== "" && nameTaken(others, trimmed, search.id);
  const canSave = trimmed !== "" && !taken && trimmed !== search.name;
  return (
    <div className="flex flex-col gap-2.5">
      <NameField label="Nuovo nome" value={name} onChange={setName} />
      {taken && <PanelStatus role="note">{NAME_TAKEN}</PanelStatus>}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button size="sm" isDisabled={!canSave} onPress={() => onSave(trimmed)} className={STATUS_BUTTON_CLASS}>
          Salva
        </Button>
        <Button size="sm" onPress={onCancel} className={STATUS_BUTTON_CLASS}>
          Annulla
        </Button>
      </div>
    </div>
  );
}

function Move({
  search,
  folders,
  onSave,
  onCancel,
}: {
  search: SavedSearch;
  folders: string[];
  onSave: (folder: string | null) => void;
  onCancel: () => void;
}) {
  const [folder, setFolder] = useState(search.folder ?? "");
  const trimmed = folder.trim();
  return (
    <div className="flex flex-col gap-2.5">
      <NameField label="Cartella" value={folder} onChange={setFolder} maxLength={40} />
      <FolderChoices folders={folders.filter((f) => f !== search.folder)} onPick={setFolder} />
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          size="sm"
          isDisabled={trimmed === "" || trimmed === search.folder}
          onPress={() => onSave(trimmed)}
          className={STATUS_BUTTON_CLASS}
        >
          Sposta
        </Button>
        {search.folder && (
          <Button size="sm" onPress={() => onSave(null)} className={STATUS_BUTTON_CLASS}>
            Togli dalla cartella
          </Button>
        )}
        <Button size="sm" onPress={onCancel} className={STATUS_BUTTON_CLASS}>
          Annulla
        </Button>
      </div>
    </div>
  );
}

/** «Rinomina la cartella»: the new name, to every search of the folder; the name of another folder joins the two. */
function RenameFolder({
  folder,
  folders,
  onSave,
  onCancel,
}: {
  folder: string;
  folders: string[];
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(folder);
  const trimmed = name.trim();
  const joins = trimmed !== folder && folders.some((f) => f.toLowerCase() === trimmed.toLowerCase());
  return (
    <div className="flex flex-col gap-2.5">
      <NameField label="Nuovo nome della cartella" value={name} onChange={setName} maxLength={40} />
      {joins && <PanelStatus role="note">{FOLDER_JOINS}</PanelStatus>}
      <div className="flex flex-wrap items-center gap-2.5">
        <Button
          size="sm"
          isDisabled={trimmed === "" || trimmed === folder}
          onPress={() => onSave(trimmed)}
          className={STATUS_BUTTON_CLASS}
        >
          Salva
        </Button>
        <Button size="sm" onPress={onCancel} className={STATUS_BUTTON_CLASS}>
          Annulla
        </Button>
      </div>
    </div>
  );
}

type ItemProps = {
  search: SavedSearch;
  mode: Mode;
  setMode: (mode: Mode) => void;
  failure: Failure | null;
  others: SavedSearch[];
  folders: string[];
  onRename: (name: string) => void;
  onMove: (folder: string | null) => void;
  onDelete: () => void;
};

/** One saved search: the white field that reopens it, its tags, its menu. */
function SavedItem({ search, mode, setMode, failure, others, folders, onRename, onMove, onDelete }: ItemProps) {
  const renaming = mode.kind === "rename" && mode.id === search.id;
  const moving = mode.kind === "move" && mode.id === search.id;
  const confirming = mode.kind === "confirm-delete" && mode.id === search.id;
  return (
    <li className="flex flex-col gap-2.5">
      {renaming ? (
        <Rename search={search} others={others} onSave={onRename} onCancel={() => setMode({ kind: "idle" })} />
      ) : (
        <div className="flex items-center gap-2">
          <Link
            to="/ricerca"
            state={savedDraftState(search, writeSearchParams(fromSavedQuery(search.query)))}
            className="flex h-9 min-w-0 items-center rounded-xl bg-field-background px-3 text-sm leading-5 text-field-placeholder shadow-field"
          >
            <span className="truncate">{search.name}</span>
          </Link>
          <Dropdown>
            <Dropdown.Trigger aria-label={`Azioni per «${search.name}»`} className="cursor-pointer rounded-sm">
              <Icon name="chevronDown" />
            </Dropdown.Trigger>
            <Dropdown.Popover placement="bottom end" className="min-w-48 rounded-xl">
              <Dropdown.Menu
                aria-label={`Azioni per «${search.name}»`}
                onAction={(key) => {
                  if (key === "rename") setMode({ kind: "rename", id: search.id });
                  else if (key === "move") setMode({ kind: "move", id: search.id });
                  else if (key === "delete") setMode({ kind: "confirm-delete", id: search.id });
                }}
              >
                <Dropdown.Item id="rename" textValue="Rinomina" className="rounded-[20px] px-3 py-1.5">
                  <Label className="text-sm leading-6 font-medium text-accent">Rinomina</Label>
                </Dropdown.Item>
                <Dropdown.Item id="move" textValue="Sposta in una cartella" className="rounded-[20px] px-3 py-1.5">
                  <Label className="text-sm leading-6 font-medium text-accent">Sposta in una cartella</Label>
                </Dropdown.Item>
                <Dropdown.Item id="delete" textValue="Elimina" className="rounded-[20px] px-3 py-1.5">
                  <Label className="text-sm leading-6 font-medium text-accent">Elimina</Label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      )}
      <ul aria-label={`Scelte di «${search.name}»`} className="flex flex-wrap items-center gap-1.5">
        {savedSearchTags(search).map((tag) => (
          <li
            key={tag}
            className="flex h-5 items-center rounded-xl bg-white px-1 text-xs leading-5 font-medium text-field-foreground"
          >
            {tag}
          </li>
        ))}
      </ul>
      {moving && <Move search={search} folders={folders} onSave={onMove} onCancel={() => setMode({ kind: "idle" })} />}
      {confirming && (
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-xs leading-5 text-muted">{CONFIRM_DELETE}</span>
          <Button size="sm" onPress={onDelete} className={STATUS_BUTTON_CLASS}>
            Elimina
          </Button>
          <Button size="sm" onPress={() => setMode({ kind: "idle" })} className={STATUS_BUTTON_CLASS}>
            Annulla
          </Button>
        </div>
      )}
      {failure && (
        <PanelStatus role="alert" indent={false} onRetry={failure.onRetry}>
          {failure.message}
        </PanelStatus>
      )}
      <Separator variant="secondary" className="my-[5.5px]" />
    </li>
  );
}

export function SavedSearchesPanel({ onCollapse }: { onCollapse: () => void }) {
  const list = useSavedSearches();
  const change = useChangeSavedSearch();
  const remove = useDeleteSavedSearch();
  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const [failure, setFailure] = useState<Failure | null>(null);

  const sendChange = (id: string, body: { name?: string; folder?: string | null }, action: SavedAction) => {
    setMode({ kind: "idle" });
    setFailure((f) => (f?.id === id ? null : f));
    change.mutateAsync({ id, ...body }).catch((error: unknown) => {
      const { message, retry } = savedSearchError(error, action);
      setFailure({ id, message, ...(retry ? { onRetry: () => sendChange(id, body, action) } : {}) });
    });
  };

  const sendDelete = (id: string) => {
    setMode({ kind: "idle" });
    setFailure((f) => (f?.id === id ? null : f));
    remove.mutateAsync(id).catch((error: unknown) => {
      const { message, retry } = savedSearchError(error, "delete");
      setFailure({ id, message, ...(retry ? { onRetry: () => sendDelete(id) } : {}) });
    });
  };

  let body: React.ReactNode;
  if (list.data === undefined) {
    body =
      list.isError && !list.isFetching ? (
        <PanelStatus role="alert" onRetry={() => void list.refetch()}>
          {SAVED_FAILED}
        </PanelStatus>
      ) : (
        <PanelStatus>{LOADING_SAVED}</PanelStatus>
      );
  } else if (list.data.items.length === 0) {
    body = <PanelStatus>{NO_SAVED}</PanelStatus>;
  } else {
    const items = list.data.items;
    const { folders, loose } = savedSearchFolders(items);
    const folderNames = folders.map((f) => f.name);
    const item = (search: SavedSearch) => (
      <SavedItem
        key={search.id}
        search={search}
        mode={mode}
        setMode={setMode}
        failure={failure?.id === search.id ? failure : null}
        others={items}
        folders={folderNames}
        onRename={(name) => sendChange(search.id, { name }, "rename")}
        onMove={(folder) => sendChange(search.id, { folder }, "move")}
        onDelete={() => sendDelete(search.id)}
      />
    );
    body = (
      <div className="flex flex-col gap-2.5">
        {folders.map((folder) => (
          <SidebarSection key={folder.name} title={folder.name} icon="folder" openIcon="folderOpen">
            {mode.kind === "rename-folder" && mode.folder === folder.name ? (
              <RenameFolder
                folder={folder.name}
                folders={folderNames}
                onSave={(name) => {
                  for (const search of folder.items) sendChange(search.id, { folder: name }, "move");
                }}
                onCancel={() => setMode({ kind: "idle" })}
              />
            ) : (
              <button
                type="button"
                onClick={() => setMode({ kind: "rename-folder", folder: folder.name })}
                className="flex w-fit cursor-pointer items-center gap-1 rounded-sm text-xs leading-5 font-medium text-accent underline-offset-2 hover:underline"
              >
                Rinomina la cartella
              </button>
            )}
            <ul className="flex flex-col gap-2.5">{folder.items.map(item)}</ul>
          </SidebarSection>
        ))}
        {loose.length > 0 && <ul className="flex flex-col gap-2.5">{loose.map(item)}</ul>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[25px]">
      <div className="flex items-center justify-between">
        <h2 className="text-xl leading-7 font-semibold text-brand">{SAVED_SEARCHES_TITLE}</h2>
        <PanelToggle title={SAVED_SEARCHES_TITLE} onPress={onCollapse} />
      </div>
      {list.data !== undefined && list.isError && !list.isFetching && (
        <PanelStatus role="alert" onRetry={() => void list.refetch()}>
          Aggiornamento delle ricerche salvate non riuscito.
        </PanelStatus>
      )}
      {failure && !list.data?.items.some((s) => s.id === failure.id) && (
        <PanelStatus role="alert" indent={false} onRetry={failure.onRetry}>
          {failure.message}
        </PanelStatus>
      )}
      {body}
    </div>
  );
}
